import React, { useCallback, useEffect, useReducer, useState } from 'react'
import { useDocumentTitle } from '../../../../common/useDocumentTitle'

import { useTracking, OnboardingTrackEvent } from '../common/tracking'
import '../FinancialAdvisorSummary.scss'
import classNames from 'classnames'
import { Button } from '../../../../components/Button/Button'
import { addAIContext, aiContextReducer, removeAIContext, setAIFeatureIdInEdit } from '../state/reducer'
import { ActionableAIFeatureList, EmailActionAIResult } from '../AIResultsViews'
import { CopyActionAIResult } from '../CopyActionAIResult'
import { AIContextContext } from '../state/context'
import { initialAIContextState } from '../state/state'
import { aiFeaturePresets, aiFeaturesDemoData, aiResultResponse, demoSession } from './DemoMeetingData'
import { AIResultDisplayModel, AIResultViewModel, mapAiResultsToAiResultViewModel } from '../AIResultMapper'
import { copyAllAIFeatureResults, updateAIResultsProperties } from '../OndemandAIExecution'
import { AIFeature } from '@zocks-communications/client-sdk-js'
import { DemoSessionContext, RoomSessionContext } from '../models'
import { EditableText } from '../common/EditableText/EditableText'
import { formatDate, formatTime } from '../common/aifeatures/utils'
import { SessionSummaryParticipants } from '../common/SessionSummaryParticipants'
import { Toast, ToastContainer } from 'react-bootstrap'
import { SessionShareButton } from '../../../../components/Button/ShareButton'
import { AiFeature } from '../../../../models/AiFeature'
import { AiResult } from '../../../../models/AiResult'
import { parseMetadata } from '../../../../common/util'

export const DemoMeeting: React.FC = () => {
  useDocumentTitle('Demo Meeting')
  const { trackingClient } = useTracking()

  useEffect(() => {
    trackingClient.track({ eventName: OnboardingTrackEvent.DEMO_MEETING_VIEWED })
  }, [trackingClient])

  const [copyBlockOpened, setCopyBlockOpened] = useState<boolean>(false)
  const [emailBlockOpened, setEmailBlockOpened] = useState<boolean>(false)
  const [aiFeatures] = useState<AIFeature[]>(aiFeaturesDemoData)
  const [aiResults, setAiResults] = useState<AIResultViewModel[]>(
    mapAiResultsToAiResultViewModel(aiResultResponse as AiResult[], aiFeatures as AiFeature[], aiFeaturePresets),
  )
  const [showCopyToast, setShowCopyToast] = useState<boolean>(false)

  const onToggleEmailBlockOpened = () => {
    setEmailBlockOpened((opened: boolean) => !opened)
  }

  const onToggleCopyBlockOpened = () => {
    setCopyBlockOpened((opened: boolean) => !opened)
  }

  const onDismissAIResult = (aiResult: AIResultDisplayModel) => {
    const results = updateAIResultsProperties({ aiResult, aiResults, propertyToUpdate: 'dismissed' })
    setAiResults(results)
  }

  const onSelectAIResult = (aiResult: AIResultDisplayModel) => {
    const results = updateAIResultsProperties({ aiResult, aiResults, propertyToUpdate: 'selected' })
    if (aiResult.selected) {
      dispatch(addAIContext(aiResult))
    } else {
      dispatch(removeAIContext(aiResult.displayId))
    }
    setAiResults(results)
  }

  const shouldShowAllActionButton = useCallback(
    (type: 'email' | 'copy') => {
      if (!aiResults.length || !aiFeatures.length) {
        return false
      }
      const aiFeaturesIds = aiFeatures
        .filter((aiFeature: AIFeature) => parseMetadata(aiFeature.metadata).actionType === type)
        .map((aiFeature: AIFeature) => aiFeature.id)

      return aiResults.map((result: AIResultViewModel) => result.aiFeatureId).some((aiFeatureId: string) => aiFeaturesIds.includes(aiFeatureId))
    },
    [aiResults, aiFeatures],
  )

  const onCopyAllAIFeatureResults = async () => {
    await copyAllAIFeatureResults(aiFeatures, aiResults, demoSession)
    setShowCopyToast(true)
  }
  const metadata = demoSession ? parseMetadata(demoSession.metadata) : {}
  const externalMetadata = metadata.external || {}

  const sessionDurationInSeconds = (new Date(demoSession.finishedAt).getTime() - new Date(demoSession.startedAt!).getTime()) / 1000
  const sessionDuration = formatTime(sessionDurationInSeconds)

  const [state, dispatch] = useReducer(aiContextReducer, initialAIContextState)
  return (
    <DemoSessionContext.Provider value={{ demoModeEnabled: true }}>
      <RoomSessionContext.Provider value={demoSession}>
        <div>
          <ToastContainer position='top-center' className='p-3 summary-toast'>
            <Toast onClose={() => setShowCopyToast(false)} show={showCopyToast} delay={1500} autohide={false}>
              <Toast.Body>Copied to clipboard!</Toast.Body>
            </Toast>
          </ToastContainer>
          <div className='full-size-content demo-meeting'>
            <div>
              <div>
                <div>
                  <div className='standalone-end-meeting-header'>
                    <h4>Meeting Summary</h4>
                  </div>
                  <div className='standalone-end-meeting-body'>
                    <div className='w-100'>
                      <div className='financial-advisor-summary-wrapper'>
                        <div className='toast-container top-0 end-0 p-3' style={{ zIndex: 1 }}></div>
                        <div className='financial-advisor-summary'>
                          <div className='financial-advisor-session-details w-50'>
                            <h4>
                              <div className='d-flex gap-2 align-items-center'>
                                <div className='flex-shrink-0 lead-medium'>Meeting title:</div>
                                <EditableText text={externalMetadata.title} inputPlaceHolder={'Enter meeting title'} />
                              </div>
                            </h4>
                            <div>
                              <span className='lead-medium'>Started at: </span>
                              <span className='paragraph-medium'>{demoSession?.startedAt ? formatDate(demoSession?.startedAt) : 'Unknown'}</span>
                            </div>
                            <div>
                              <span className='lead-medium'>Finished at: </span>
                              <span className='paragraph-medium'>{demoSession?.finishedAt ? formatDate(demoSession.finishedAt.toString()) : 'Unknown'}</span>
                            </div>
                            <div>
                              <span className='lead-medium'>Duration: </span>
                              <span className='paragraph-medium'>{sessionDuration}</span>
                            </div>
                            <div className='d-flex align-items-center mt-4 gap-2'>
                              <SessionShareButton variant='darkGhost' roomSession={demoSession} />
                            </div>
                          </div>
                          <div className='financial-advisor-user-details'>
                            <SessionSummaryParticipants
                              roomSession={demoSession}
                              availableAIFeatureTypes={[]}
                              handleSelectUser={() => {}}
                              roomSessionLoading={false}
                              showAllocationButton={false}
                            />
                          </div>
                        </div>
                        <label>Summary</label>
                        <AIContextContext.Provider value={{ state, dispatch }}>
                          <div
                            className={classNames('financial-advisor-summary-actions-border d-flex flex-column', {
                              'editing-in-progress': state.aiFeatureIdInEdit,
                            })}
                          >
                            {state.aiFeatureIdInEdit && (
                              <div>
                                <div className='alert alert-warning text-left pick-result-alert' role='alert'>
                                  <div className='pick-result-alert--text'>
                                    <i className='bi bi-lightning-fill'></i> You are picking results.
                                  </div>
                                  <Button
                                    variant='primary'
                                    onClick={() => {
                                      dispatch(setAIFeatureIdInEdit(null))
                                    }}
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            )}
                            {demoSession && (
                              <div className='d-flex gap-3 pt-3 zocks-mb-0.75'>
                                {shouldShowAllActionButton('copy') && (
                                  <div className='d-flex flex-1 justify-content-between w-50'>
                                    <div className='ai-feature-copy-all-action'>
                                      <div className='cursor-pointer' onClick={onCopyAllAIFeatureResults}>
                                        Copy all results to clipboard
                                      </div>
                                    </div>
                                    <div className='ai-feature-toggle-all-action'>
                                      <div className='cursor-pointer' onClick={onToggleCopyBlockOpened}>
                                        {copyBlockOpened ? 'Close All' : 'Open All'}
                                      </div>
                                    </div>
                                  </div>
                                )}
                                {shouldShowAllActionButton('email') && (
                                  <div className='flex-grow-1 d-flex justify-content-end'>
                                    <div className='ai-feature-toggle-all-action'>
                                      <div className='cursor-pointer' onClick={onToggleEmailBlockOpened}>
                                        {emailBlockOpened ? 'Close All' : 'Open All'}
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}

                            <div className='financial-advisor-summary-actions'>
                              <div className='financial-advisor-summary-action-wrapper'>
                                <ActionableAIFeatureList
                                  actionType={'copy'}
                                  ActionableAIResultElement={CopyActionAIResult}
                                  aiFeatures={aiFeatures}
                                  onRerunAIFeature={() => {}}
                                  onDismissAIResult={onDismissAIResult}
                                  aiResults={aiResults}
                                  aiResultsLoading={false}
                                  resultBlockOpened={copyBlockOpened}
                                  onSelectAIResult={onSelectAIResult}
                                  aiFeaturePresets={[]}
                                />
                              </div>

                              <div className='financial-advisor-summary-action-wrapper'>
                                <ActionableAIFeatureList
                                  actionType={'email'}
                                  ActionableAIResultElement={EmailActionAIResult}
                                  aiFeatures={aiFeatures}
                                  onRerunAIFeature={() => {}}
                                  onDismissAIResult={onDismissAIResult}
                                  aiResults={aiResults}
                                  aiResultsLoading={false}
                                  resultBlockOpened={emailBlockOpened}
                                  onSelectAIResult={onSelectAIResult}
                                  aiFeaturePresets={[]}
                                />
                              </div>
                            </div>
                          </div>
                        </AIContextContext.Provider>

                        <div className='d-flex w-100'>
                          <Button className='w-100' variant={'danger'} disabled={true} onClick={() => {}}>
                            Delete meeting
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </RoomSessionContext.Provider>
    </DemoSessionContext.Provider>
  )
}
